import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';
import { DollarSign, RotateCcw, Save, Info } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const FIELDS = [
  {
    key: 'valor_fala',
    label: 'Valor da fala',
    suffix: 'R$ por fala',
    help: 'Multiplicado por num_falas para calcular o valor de cada personagem do dublador.',
  },
  {
    key: 'valor_orcamento',
    label: 'Orçamento (base)',
    suffix: 'R$ por minuto (referência)',
    help: 'Valor de referência usado em cotações/propostas para o cliente.',
  },
  {
    key: 'valor_gerente',
    label: 'Direção (Gerência)',
    suffix: '× minutos',
    help: 'Multiplicador aplicado à duração do projeto (minutos). Total ÷ Nº de gerentes.',
  },
  {
    key: 'valor_mixagem',
    label: 'Mixagem',
    suffix: '× minutos',
    help: 'Multiplicador aplicado à duração do projeto. Total ÷ Nº de mixadores.',
  },
  {
    key: 'valor_roteiro',
    label: 'Roteiro',
    suffix: '× minutos',
    help: 'Multiplicador aplicado à duração do projeto. Total ÷ Nº de roteiristas.',
  },
  {
    key: 'valor_srt',
    label: 'SRT',
    suffix: '× minutos',
    help: 'Multiplicador aplicado à duração do projeto. Total ÷ Nº de SRTs.',
  },
];

const BaseValuesEditor = () => {
  const { user, getAuthHeaders } = useAuth();
  const [values, setValues] = useState(null);
  const [defaults, setDefaults] = useState(null);
  const [draft, setDraft] = useState({});
  const [saving, setSaving] = useState(false);

  const isDono = user?.role === 'dono';

  useEffect(() => {
    let cancel = false;
    axios.get(`${API}/base-values`, { headers: getAuthHeaders() })
      .then(res => {
        if (cancel) return;
        setValues(res.data?.values || {});
        setDefaults(res.data?.defaults || {});
        setDraft(res.data?.values || {});
      })
      .catch(() => {});
    return () => { cancel = true; };
  }, [getAuthHeaders]);

  const getField = (f) => f.key;

  const changed = values && Object.keys(draft).some(k => {
    const cur = Number(values[k] ?? 0);
    const next = Number(draft[k] ?? 0);
    return Math.abs(cur - next) > 0.0001;
  });

  const onChange = (key, val) => {
    if (val === '' || val === '-') {
      setDraft(d => ({ ...d, [key]: val }));
      return;
    }
    const num = parseFloat(val.replace(',', '.'));
    if (Number.isNaN(num)) return;
    setDraft(d => ({ ...d, [key]: num }));
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = {};
      Object.keys(draft).forEach(k => {
        const n = typeof draft[k] === 'number' ? draft[k] : parseFloat(String(draft[k]).replace(',', '.'));
        if (!Number.isNaN(n)) payload[k] = n;
      });
      const res = await axios.put(`${API}/base-values`, payload, { headers: getAuthHeaders() });
      setValues(res.data?.values || {});
      setDraft(res.data?.values || {});
      toast.success('Valores base atualizados!');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao salvar valores base');
    } finally {
      setSaving(false);
    }
  };

  const resetField = (key) => {
    if (!defaults) return;
    setDraft(d => ({ ...d, [key]: defaults[key] }));
  };

  if (!values) {
    return (
      <div className="bg-slate-800/50 rounded-xl border border-slate-700 p-6 text-slate-400 text-sm">
        Carregando valores base…
      </div>
    );
  }

  return (
    <div className="bg-slate-800/50 rounded-xl border border-slate-700 overflow-hidden" data-testid="base-values-editor">
      <div className="p-4 border-b border-slate-700 flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <DollarSign className="h-5 w-5 text-emerald-400" />
          <div>
            <h2 className="text-xl font-semibold text-white">Valores Base</h2>
            <p className="text-xs text-slate-400 mt-0.5">
              {isDono
                ? 'Edite os valores que alimentam as previsões e cálculos financeiros.'
                : 'Apenas leitura — somente o Dono pode editar estes valores.'}
            </p>
          </div>
        </div>
        {isDono && changed && (
          <button
            onClick={save}
            disabled={saving}
            className="flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors"
            data-testid="base-values-save"
          >
            <Save className="h-4 w-4" />
            {saving ? 'Salvando…' : 'Salvar alterações'}
          </button>
        )}
      </div>
      <div className="p-4 grid grid-cols-1 md:grid-cols-2 gap-3">
        {FIELDS.map(f => {
          const k = getField(f);
          const v = draft[k];
          const def = defaults?.[k];
          const isModified = values && Math.abs(Number(values[k] ?? 0) - Number(v ?? 0)) > 0.0001;
          return (
            <div
              key={f.key}
              className={`rounded-lg border ${isModified ? 'border-amber-500/50 bg-amber-500/5' : 'border-slate-700 bg-slate-900/40'} p-3`}
              data-testid={`base-value-row-${f.key}`}
            >
              <div className="flex items-start justify-between gap-2">
                <div>
                  <p className="text-sm font-medium text-white">{f.label}</p>
                  <p className="text-[11px] text-slate-500 flex items-center gap-1 mt-0.5">
                    <Info className="h-3 w-3" /> {f.help}
                  </p>
                </div>
                {isDono && def !== undefined && (
                  <button
                    type="button"
                    onClick={() => resetField(k)}
                    className="text-[11px] text-slate-500 hover:text-amber-400 flex items-center gap-1 shrink-0"
                    title={`Restaurar padrão (${def})`}
                    data-testid={`base-value-reset-${f.key}`}
                  >
                    <RotateCcw className="h-3 w-3" /> {def}
                  </button>
                )}
              </div>
              <div className="mt-2 flex items-center gap-2">
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  disabled={!isDono}
                  value={v === undefined || v === null ? '' : v}
                  onChange={e => onChange(k, e.target.value)}
                  className="flex-1 bg-slate-700 border border-slate-600 rounded-lg px-3 py-1.5 text-white text-sm focus:outline-none focus:border-emerald-500 disabled:opacity-60 disabled:cursor-not-allowed font-mono"
                  data-testid={`base-value-input-${f.key}`}
                />
                <span className="text-xs text-slate-400 whitespace-nowrap">{f.suffix}</span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default BaseValuesEditor;
